/* eslint-disable no-underscore-dangle */
import classNames from 'classnames';
import {
  NotificationAction,
  NotificationReason,
  Notifications,
  NotificationType,
} from '../../../../../../components/home/types';
import { EmailAccountType } from '../../../../../../components/email-accounts/enums/email-account';
import { Images } from '../../../../../app-constants';
import {
  getAgencyConfigDetails,
  getUserRole,
  isWhitelabelWithClientView,
} from '../../../../../utils/user-details';
import { UserRole } from '../../../../../enums/user-roles';

const quoteRegex = /(“.*?”)/g;
const quoteRegexForEmail = /(”.*?”)/g;

export const getRedirectUrl = (notification: Notifications) => {
  const reactAppUrl = isWhitelabelWithClientView()
    ? getAgencyConfigDetails()?.clientBaseUrl || ''
    : process.env.REACT_APP_BASE_URL;

  const notificationRedirectionUrl = `${reactAppUrl}/email-accounts`;
  const notificationUrlParams = `emailAccountId=${notification.emailAccountId}&emailAccountType=${notification.emailAccountType}`;

  switch (notification.notificationType) {
    case NotificationType.EmailAccountDisconnected:
    case NotificationType.EmailAccountPaused:
      return notification.emailAccountType === EmailAccountType.SmtpImap
        ? `${notificationRedirectionUrl}?${notificationUrlParams}`
        : notificationRedirectionUrl;
    case NotificationType.SequencePaused:
    case NotificationType.SequencePausedBounce:
      return `${reactAppUrl}/sequence/${notification.sequenceId}/steps`;
    case NotificationType.UniboxScheduleCanceled:
      return `${reactAppUrl}/conversations/${encodeURIComponent(
        notification.emailThreadHashId,
      )}?page=1&type=draft&search=${encodeURIComponent(
        notification.prospectEmail,
      )}`;
    case NotificationType.LeadRevealCompleted:
    case NotificationType.LeadRevealCompletedNoEmailOrPhone:
      return notification.reportUrl;
    case NotificationType.ProspectImportAsync:
      return `${reactAppUrl}/crm/list?importedFile=${
        notification?.metaData?.importFile?.id ?? ''
      }&notificationId=${notification._id ?? ''}`;
    case NotificationType.SequenceProspectImportAsync:
      return `${reactAppUrl}/sequence/${
        notification?.sequenceId ?? ''
      }/prospects?importedFile=${
        notification?.metaData?.importFile?.id ?? ''
      }&notificationId=${notification._id ?? ''}`;
    case NotificationType.LeadRevealCompletedAsync:
    case NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync:
      return (
        notification.reportUrl ?? `${reactAppUrl}/leads?start=1&take=25#saved`
      );
    case NotificationType.LeadRevealV2EmailAsync:
    case NotificationType.LeadRevealV2EmailPhoneAsync:
    case NotificationType.LeadRevealV2PhoneAsync:
      return notification.reportUrl ?? `${reactAppUrl}/v2/leads/people/saved`;
    case NotificationType.CompanyEnrichV2Async:
    case NotificationType.CompanyRevealV2Async:
    case NotificationType.CompanyEnrichFreeV2Async:
      return (
        (notification?.reportUrl || notification.metaData?.reportUrl) ??
        `${reactAppUrl}/v2/leads/company/saved`
      );
    case NotificationType.DomainRegistrationComplete:
      return notification.reportUrl;
    case NotificationType.EmailVerifier:
      return `${reactAppUrl}/email-verifier`;
    case NotificationType.SequencePausedLowInboxPlacementRate:
      return `${reactAppUrl}/inbox-radar/report/${notification?.metaData?.templateId}`;
    case NotificationType.EmailAccountPausedLowInboxPlacementRate:
      return `${reactAppUrl}/inbox-radar/report/${notification?.metaData?.templateId}`;
    case NotificationType.AiColumns:
      return `${reactAppUrl}/crm/list`;
    case NotificationType.AiColumnsFailed:
      return `${reactAppUrl}/crm/list`;
    case NotificationType.LinkedInAutomationDisconnected:
      return notification.reportUrl ?? `${reactAppUrl}/tasks?status=failed`;
    case NotificationType.LinkedInAutomationApiKeyInvalid:
      return `${reactAppUrl}/settings/integrations`;
    default:
      return '';
  }
};

export const moreNotificationExist = (
  notificationsLength: number,
  notificationCount: number,
) => notificationsLength < notificationCount;

export const getNotificationType = (notification: Notifications) => {
  const userRole = getUserRole() as UserRole;
  const clientRole = [
    UserRole.ClientFullAccess,
    UserRole.ClientRestrictedAccess,
    UserRole.ClientNoAccess,
  ];
  if (
    notification.notificationType === NotificationType.UniboxScheduleCanceled
  ) {
    return NotificationAction.ViewDraft;
  }
  if (notification.notificationType === NotificationType.WhitelabelDomainSet) {
    return NotificationAction.Login;
  }
  if (
    notification.notificationType === NotificationType.LeadRevealCompleted ||
    notification.notificationType ===
      NotificationType.LeadRevealCompletedNoEmailOrPhone
  ) {
    return NotificationAction.Leads;
  }

  if (
    notification.notificationType ===
      NotificationType.SequencePausedLowInboxPlacementRate ||
    notification.notificationType ===
      NotificationType.EmailAccountPausedLowInboxPlacementRate
  ) {
    return NotificationAction.ViewReport;
  }

  if (notification?.reportUrl) {
    const proscpectNotifications = [
      NotificationType.SequenceProspectImportAsync,
      NotificationType.ProspectImportAsync,
    ];

    const leadNotifications = [
      NotificationType.LeadRevealCompletedAsync,
      NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync,
      NotificationType.LeadRevealV2EmailAsync,
      NotificationType.LeadRevealV2EmailPhoneAsync,
      NotificationType.LeadRevealV2PhoneAsync,
    ];

    const companyNotifications = [
      NotificationType.CompanyEnrichV2Async,
      NotificationType.CompanyRevealV2Async,
      NotificationType.CompanyEnrichFreeV2Async,
    ];

    if (proscpectNotifications?.includes(notification.notificationType)) {
      return NotificationAction.ViewList;
    }

    if (companyNotifications?.includes(notification.notificationType)) {
      return NotificationAction.View;
    }

    if (leadNotifications?.includes(notification.notificationType)) {
      return NotificationAction.ViewLeads;
    }

    if (notification.notificationType === NotificationType.NoteMention) {
      return NotificationAction.ViewNote;
    }

    return NotificationAction.Download;
  }

  if (
    notification.notificationType === NotificationType.SequenceProspectImport
  ) {
    return '';
  }

  if (notification.notificationType === NotificationType.EmailAccountPaused) {
    return NotificationAction.Activate;
  }

  if (
    notification.notificationType === NotificationType.ProspectImportAsync ||
    notification.notificationType ===
      NotificationType.SequenceProspectImportAsync
  ) {
    return NotificationAction.ViewList;
  }

  if (
    notification.notificationType ===
      NotificationType.LeadRevealCompletedAsync ||
    notification.notificationType ===
      NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
    notification.notificationType === NotificationType.LeadRevealV2EmailAsync ||
    notification.notificationType ===
      NotificationType.LeadRevealV2EmailPhoneAsync ||
    notification.notificationType === NotificationType.LeadRevealV2PhoneAsync ||
    notification.notificationType === NotificationType.CompanyEnrichV2Async ||
    notification.notificationType === NotificationType.CompanyRevealV2Async ||
    notification.notificationType === NotificationType.CompanyEnrichFreeV2Async
  ) {
    return NotificationAction.View;
  }
  if (
    notification.notificationType ===
    NotificationType.DomainRegistrationComplete
  ) {
    return NotificationAction.ViewEmailAccounts;
  }
  if (notification.notificationType === NotificationType.EmailVerifier) {
    if (notification?.completedAt) {
      return NotificationAction.ViewList;
    }
    return NotificationAction.View;
  }
  if (notification.notificationType === NotificationType.AiColumns) {
    if (clientRole?.includes(userRole)) {
      return '';
    }
    if (notification?.completedAt) {
      return NotificationAction.ViewList;
    }
    return NotificationAction.View;
  }
  if (notification.notificationType === NotificationType.AiColumnsFailed) {
    if (clientRole?.includes(userRole)) {
      return '';
    }
    return NotificationAction.viewFailed;
  }

  if (
    notification.notificationType ===
      NotificationType.LinkedInAutomationDisconnected ||
    notification.notificationType ===
      NotificationType.LinkedInAutomationApiKeyInvalid
  ) {
    return NotificationAction.viewFailed;
  }

  return notification.notificationType ===
    NotificationType.EmailAccountDisconnected
    ? NotificationAction.Reconnect
    : NotificationAction.Resume;
};

export const getRegexForEmail = (notification: Notifications) =>
  notification.notificationType === NotificationType.EmailAccountDisconnected
    ? quoteRegexForEmail
    : quoteRegex;

export const getNotificationReason = (notification: Notifications) => {
  if (
    notification.notificationType === NotificationType.UniboxScheduleCanceled
  ) {
    return NotificationReason.UniboxScheduleCanceled;
  }
  if (
    notification.notificationType ===
    NotificationType.SequenceProspectExportForClient
  ) {
    return NotificationReason.SequenceProspectExportForClient;
  }
  if (notification.notificationType === NotificationType.SequenceReportClient) {
    return NotificationReason.SequenceReportClient;
  }
  if (
    notification.notificationType ===
    NotificationType.UniboxConversationForClient
  ) {
    return NotificationReason.UniboxConversationForClient;
  }
  if (
    notification.notificationType ===
    NotificationType.EmailAccountBulkConnectClient
  ) {
    return NotificationReason.EmailAccountBulkConnectClient;
  }
  if (
    notification.notificationType ===
    NotificationType.SequenceBulkProspectClient
  ) {
    return NotificationReason.SequenceBulkProspectClient;
  }
  if (notification.notificationType === NotificationType.WhitelabelDomainSet) {
    return NotificationReason.WhitelabelDomainSet;
  }
  if (notification.notificationType === NotificationType.DnsImport) {
    return NotificationReason.DnsImport;
  }
  if (notification.notificationType === NotificationType.DnsExport) {
    return NotificationReason.DnsExport;
  }
  if (
    notification.notificationType === NotificationType.SequenceProspectImport
  ) {
    return NotificationReason.SequenceProspectImport;
  }
  if (notification.notificationType === NotificationType.LeadRevealCompleted) {
    return NotificationReason.LeadRevealCompleted;
  }
  if (
    notification.notificationType ===
    NotificationType.LeadRevealCompletedNoEmailOrPhone
  ) {
    return NotificationReason.LeadRevealCompletedNoEmailOrPhone;
  }
  if (
    notification.notificationType === NotificationType.SequenceProspectVerify ||
    notification.notificationType === NotificationType.ProspectVerifyReport
  ) {
    return NotificationReason.ProspectVerifyReport;
  }
  if (
    notification.notificationType ===
    NotificationType.SequenceProspectBulkAction
  ) {
    return NotificationReason.SequenceProspectBulkAction;
  }

  if (notification.notificationType === NotificationType.ProspectBulkAction) {
    return NotificationReason.ProspectBulkAction;
  }

  if (notification.notificationType === NotificationType.EmailAccountPaused) {
    return NotificationReason.EmailAccountPaused;
  }

  if (notification.notificationType === NotificationType.ProspectImportAsync) {
    if (notification?.completedAt) {
      return NotificationReason.ProspectImportAsyncCompleted;
    }
    return NotificationReason.ProspectImportAsync;
  }
  if (
    notification.notificationType ===
    NotificationType.DomainRegistrationInProgress
  ) {
    return NotificationReason.DomainRegistrationInProgress;
  }

  if (
    notification.notificationType ===
    NotificationType.MailboxRegistrationComplete
  ) {
    return NotificationReason.MailboxRegistrationComplete;
  }

  if (
    notification.notificationType ===
    NotificationType.SequenceProspectImportAsync
  ) {
    if (notification?.completedAt) {
      return NotificationReason.SequenceProspectImportAsyncCompleted;
    }
    return NotificationReason.SequenceProspectImportAsync;
  }

  if (
    notification.notificationType === NotificationType.LeadRevealCompletedAsync
  ) {
    if (notification?.completedAt) {
      return NotificationReason.LeadRevealCompletedAsyncProcess;
    }
    return NotificationReason.LeadRevealCompletedAsync;
  }

  if (
    notification.notificationType === NotificationType.LeadRevealV2EmailAsync ||
    notification.notificationType ===
      NotificationType.LeadRevealV2EmailPhoneAsync ||
    notification.notificationType === NotificationType.LeadRevealV2PhoneAsync
  ) {
    if (notification?.completedAt) {
      return NotificationReason.LeadRevealCompletedAsyncProcess;
    }
    return NotificationReason.LeadRevealCompletedAsync;
  }

  if (notification.notificationType === NotificationType.CompanyEnrichV2Async) {
    if (notification?.completedAt) {
      return NotificationReason.CompanyEnrichV2AsyncProcess;
    }
    return NotificationReason.CompanyEnrichV2Async;
  }

  if (notification.notificationType === NotificationType.CompanyRevealV2Async) {
    if (notification?.completedAt) {
      return NotificationReason.CompanyRevealV2AsyncProcess;
    }
    return NotificationReason.CompanyRevealV2Async;
  }

  if (
    notification.notificationType === NotificationType.CompanyEnrichFreeV2Async
  ) {
    if (notification?.completedAt) {
      return NotificationReason.CompanyEnrichFreeV2AsyncProcess;
    }
    return NotificationReason.CompanyEnrichFreeV2Async;
  }

  if (
    notification.notificationType ===
    NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync
  ) {
    if (notification?.completedAt) {
      return NotificationReason.LeadRevealCompletedNoEmailOrPhoneAsyncProcess;
    }
    return NotificationReason.LeadRevealCompletedNoEmailOrPhoneAsync;
  }

  if (
    notification?.notificationType ===
    NotificationType.DomainRegistrationComplete
  ) {
    return NotificationReason.DomainRegistrationComplete;
  }

  if (
    notification.notificationType ===
    NotificationType.ActiveProspectLimitExhausted
  ) {
    return NotificationReason.ActiveProspectLimitExhausted;
  }

  if (notification.notificationType === NotificationType.SnoozeTasks) {
    return NotificationReason.SnoozeTasks;
  }

  if (notification.notificationType === NotificationType.SkipTasks) {
    return NotificationReason.SkipTasks;
  }

  if (notification.notificationType === NotificationType.NoteMention) {
    return NotificationReason.NoteMention;
  }

  if (notification.notificationType === NotificationType.EmailVerifier) {
    if (notification?.completedAt) {
      return NotificationReason.EmailVerificationCompleted;
    }
    return NotificationReason.EmailVerificationInProgress;
  }

  if (
    notification.notificationType ===
    NotificationType.SequencePausedLowInboxPlacementRate
  ) {
    return NotificationReason.SequencePausedLowInboxPlacementRate;
  }

  if (
    notification.notificationType ===
    NotificationType.EmailAccountPausedLowInboxPlacementRate
  ) {
    return NotificationReason.EmailAccountPausedLowInboxPlacementRate;
  }

  if (notification.notificationType === NotificationType.AiColumns) {
    if (notification?.completedAt) {
      return NotificationReason.AiColumnsCompleted;
    }
    return NotificationReason.AiColumnsInProgress;
  }
  if (notification.notificationType === NotificationType.AiColumnsFailed) {
    return NotificationReason.AiColumnsFailed;
  }

  if (
    notification.notificationType ===
    NotificationType.LinkedInAutomationDisconnected
  ) {
    return NotificationReason.LinkedInAutomationDisconnected;
  }

  if (
    notification.notificationType ===
    NotificationType.LinkedInAutomationApiKeyInvalid
  ) {
    return NotificationReason.LinkedInAutomationApiKeyInvalid;
  }

  return notification.notificationType ===
    NotificationType.EmailAccountDisconnected
    ? NotificationReason.EmailDisconneted
    : NotificationReason.SequencePaused;
};

export const getImageIcon = (notification: Notifications) => {
  if (
    notification.notificationType === NotificationType.LeadRevealCompleted ||
    notification.notificationType ===
      NotificationType.LeadRevealCompletedNoEmailOrPhone ||
    ((notification.notificationType ===
      NotificationType.LeadRevealCompletedAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2EmailAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2EmailPhoneAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2PhoneAsync ||
      notification.notificationType === NotificationType.CompanyEnrichV2Async ||
      notification.notificationType === NotificationType.CompanyRevealV2Async ||
      notification.notificationType ===
        NotificationType.CompanyEnrichFreeV2Async) &&
      notification?.completedAt)
  ) {
    return Images.NotificationLeadBulkReveal;
  }

  if (
    (notification.notificationType === NotificationType.ProspectImportAsync ||
      notification.notificationType ===
        NotificationType.SequenceProspectImportAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealCompletedAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2EmailAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2EmailPhoneAsync ||
      notification.notificationType ===
        NotificationType.LeadRevealV2PhoneAsync ||
      notification.notificationType === NotificationType.CompanyEnrichV2Async ||
      notification.notificationType === NotificationType.CompanyRevealV2Async ||
      notification.notificationType ===
        NotificationType.CompanyEnrichFreeV2Async ||
      notification.notificationType === NotificationType.EmailVerifier ||
      notification.notificationType === NotificationType.AiColumns) &&
    !notification?.completedAt
  ) {
    return Images.NotificationRefresh;
  }
  if (
    notification.notificationType === NotificationType.ProspectImportAsync ||
    notification.notificationType ===
      NotificationType.SequenceProspectImportAsync
  ) {
    return Images.NotificationUser;
  }
  if (
    notification.notificationType ===
    NotificationType.DomainRegistrationInProgress
  ) {
    return Images.InfoCircleYellow;
  }
  if (notification.notificationType === NotificationType.EmailVerifier) {
    return Images.EmailVerifierSuccess;
  }
  if (notification.notificationType === NotificationType.AiColumns) {
    return Images.AiColumnsSuccess;
  }
  if (notification.notificationType === NotificationType.AiColumnsFailed) {
    return Images.AiColumnsFailed;
  }
  if (notification.notificationType === NotificationType.NoteMention) {
    return Images.NoteMention;
  }

  return [
    NotificationType.EmailAccountDisconnected,
    NotificationType.UniboxScheduleCanceled,
    NotificationType.ActiveProspectLimitExhausted,
    NotificationType.LinkedInAutomationDisconnected,
    NotificationType.LinkedInAutomationApiKeyInvalid,
  ].includes(notification.notificationType)
    ? Images.NotificationAlert
    : Images.NotificationPause;
};

export const getImageIconForBulkActionCompleted = () => Images.CheckGreen;

export const getNotificationClassName = (
  showNotifications: boolean,
  showBlink?: boolean,
) =>
  classNames([
    'notification-circle d-flex bs-badge hover-on-header',
    {
      'active-notification': showNotifications,
    },
    {
      'blink-notification': showBlink,
    },
  ]);

export const getNotificationTypeClass = (notification) => {
  if (
    notification?.reportUrl ||
    notification.notificationType === NotificationType.ProspectBulkAction ||
    notification.notificationType ===
      NotificationType.SequenceProspectBulkAction ||
    notification.notificationType === NotificationType.SequenceProspectImport ||
    notification.notificationType ===
      NotificationType.DomainRegistrationComplete ||
    notification.notificationType ===
      NotificationType.MailboxRegistrationComplete ||
    notification.notificationType === NotificationType.SnoozeTasks ||
    notification.notificationType === NotificationType.SkipTasks
  ) {
    return 'green'; // or 'green' if you prefer
  }

  if (
    [
      NotificationType.LeadRevealCompleted,
      NotificationType.LeadRevealCompletedNoEmailOrPhone,
      NotificationType.NoteMention,
    ].includes(notification.notificationType)
  ) {
    return 'purple';
  }

  if (
    [NotificationType.DomainRegistrationInProgress].includes(
      notification.notificationType,
    )
  ) {
    return 'yellow';
  }

  return 'default';
};

export const getNotificationClass = (notification) => {
  const notificationType = getNotificationTypeClass(notification);

  if (notificationType === 'green') {
    return 'bs-badge-status bs-badge-status-success bs-badge-dot';
  }

  if (notificationType === 'purple') {
    return 'bs-badge-purple-dot';
  }

  if (notificationType === 'yellow') {
    return 'bs-badge-yellow-dot';
  }

  return 'bs-badge-dot';
};

export const getNotificationClassNameFordotColor = (notification) => {
  const notificationType = getNotificationTypeClass(notification);

  if (notificationType === 'green') {
    return 'notification-green';
  }

  if (notificationType === 'purple') {
    return 'notification-purple';
  }

  if (notificationType === 'yellow') {
    return 'notification-yellow';
  }

  return 'notification-red';
};

export const asyncNotificationTypes = [
  NotificationType.LeadRevealCompleted,
  NotificationType.LeadRevealCompletedNoEmailOrPhone,
  NotificationType.SequenceProspectImportAsync,
  NotificationType.ProspectImportAsync,
  NotificationType.LeadRevealCompletedAsync,
  NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync,
  NotificationType.LeadRevealV2EmailAsync,
  NotificationType.LeadRevealV2EmailPhoneAsync,
  NotificationType.LeadRevealV2PhoneAsync,
  NotificationType.CompanyEnrichV2Async,
  NotificationType.CompanyRevealV2Async,
  NotificationType.CompanyEnrichFreeV2Async,
  NotificationType.EmailVerifier,
  NotificationType.AiColumns,
  NotificationType.NoteMention,
];

export const isProspectBulkOrSequenceBulk = (type: NotificationType) =>
  type !== NotificationType.ProspectBulkAction &&
  type !== NotificationType.SequenceProspectBulkAction;

export const isAsyncOperationNotification = (type: NotificationType) =>
  type === NotificationType.ProspectImportAsync ||
  type === NotificationType.SequenceProspectImportAsync ||
  type === NotificationType.LeadRevealCompletedAsync ||
  type === NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync ||
  type === NotificationType.LeadRevealV2EmailAsync ||
  type === NotificationType.LeadRevealV2EmailPhoneAsync ||
  type === NotificationType.LeadRevealV2PhoneAsync ||
  type === NotificationType.CompanyEnrichV2Async ||
  type === NotificationType.CompanyRevealV2Async ||
  type === NotificationType.CompanyEnrichFreeV2Async ||
  type === NotificationType.EmailVerifier ||
  type === NotificationType.AiColumns;

export const isLogVisibleNotificationTypes = [
  NotificationType.ProspectImportAsync,
  NotificationType.SequenceProspectImportAsync,
  NotificationType.LeadRevealCompletedAsync,
  NotificationType.LeadRevealCompletedNoEmailOrPhoneAsync,
  NotificationType.LeadRevealV2EmailAsync,
  NotificationType.LeadRevealV2EmailPhoneAsync,
  NotificationType.LeadRevealV2PhoneAsync,
  NotificationType.CompanyEnrichV2Async,
  NotificationType.CompanyRevealV2Async,
  NotificationType.CompanyEnrichFreeV2Async,
  NotificationType.SequenceBulkProspectClient,
  NotificationType.SequenceProspectExportForClient,
  NotificationType.UniboxConversationForClient,
  NotificationType.EmailAccountBulkConnectClient,
  NotificationType.SequenceReportClient,
  NotificationType.WhitelabelDomainSet,
  NotificationType.ProspectVerifyReport,
  NotificationType.SequenceProspectImport,
  NotificationType.DnsImport,
  NotificationType.DnsExport,
  NotificationType.SequenceProspectVerify,
  NotificationType.UniboxScheduleCanceled,
  NotificationType.SequenceProspectBulkAction,
  NotificationType.MailboxRegistrationComplete,
  NotificationType.EmailVerifier,
  NotificationType.AiColumns,
  NotificationType.NoteMention,
];

export const pausedNotifications = [
  NotificationType.SequencePaused,
  NotificationType.SequencePausedBounce,
  NotificationType.EmailAccountDisconnected,
  NotificationType.EmailAccountPaused,
  NotificationType.DomainRegistrationComplete,
  NotificationType.DomainRegistrationInProgress,
  NotificationType.ProspectBulkAction,
  NotificationType.LeadRevealCompleted,
  NotificationType.LeadRevealCompletedNoEmailOrPhone,
  NotificationType.ActiveProspectLimitExhausted,
  NotificationType.SnoozeTasks,
  NotificationType.SkipTasks,
  NotificationType.SequencePausedLowInboxPlacementRate,
  NotificationType.EmailAccountPausedLowInboxPlacementRate,
  NotificationType.AiColumnsFailed,
  NotificationType.LinkedInAutomationDisconnected,
  NotificationType.LinkedInAutomationApiKeyInvalid,
];

export const prospectBulkAndSequence = [
  NotificationType.ProspectBulkAction,
  NotificationType.SequenceProspectImport,
  NotificationType.SequenceProspectBulkAction,
  NotificationType.MailboxRegistrationComplete,
  NotificationType.SnoozeTasks,
  NotificationType.SkipTasks,
];

export const bulkSequenceAndDomainNotificationTypes = [
  NotificationType.ProspectBulkAction,
  NotificationType.SequenceProspectBulkAction,
  NotificationType.SequenceProspectImport,
  NotificationType.DomainRegistrationComplete,
  NotificationType.MailboxRegistrationComplete,
  NotificationType.SnoozeTasks,
  NotificationType.SkipTasks,
  NotificationType.NoteMention,
];

export const isCompletedOperationNotification = [
  NotificationType.LeadRevealCompleted,
  NotificationType.SequenceProspectImport,
  NotificationType.DomainRegistrationComplete,
  NotificationType.EmailAccountDisconnected,
  NotificationType.EmailAccountPaused,
  NotificationType.LeadRevealCompletedNoEmailOrPhone,
  NotificationType.SequencePaused,
  NotificationType.SnoozeTasks,
  NotificationType.SkipTasks,
  NotificationType.SequencePausedLowInboxPlacementRate,
  NotificationType.EmailAccountPausedLowInboxPlacementRate,
  NotificationType.LinkedInAutomationDisconnected,
  NotificationType.LinkedInAutomationApiKeyInvalid,
];
